import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import { PresentStaffChips } from './PresentStaffChips';
import { DisplayLinkMenu } from './DisplayLinkMenu';

const LEAVE_CARDS = [
  { key: 'annual', label: 'Annual leave' },
  { key: 'sick', label: 'Sick leave' },
  { key: 'emergency', label: 'Emergency leave' },
  { key: 'unpaid', label: 'Unpaid leave' },
  { key: 'maternity_paternity', label: 'Maternity / Paternity' },
  { key: 'public_holiday', label: 'Public Holiday' },
];

const RADIUS = 46;
const CIRC = 2 * Math.PI * RADIUS;

const PresentTodayPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/clock/admin/present-today');
      setData(res.data);
    } catch (e) {
      console.error('present-today load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(t);
  }, [load]);

  const pct = data?.percent || 0;
  const present = data?.present ?? 0;
  const total = data?.total ?? 0;
  const onLeave = data?.on_leave || {};
  const offset = CIRC * (1 - pct / 100);

  return (
    <div
      className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5 shadow-sm"
      data-testid="present-today-panel"
    >
      <div className="flex flex-col sm:flex-row items-center gap-6">
        {/* Progress ring */}
        <div className="relative flex-shrink-0" style={{ width: 120, height: 120 }}>
          <svg width="120" height="120" className="-rotate-90">
            <circle cx="60" cy="60" r={RADIUS} fill="none" stroke="#d1d5db" strokeWidth="10" />
            <circle
              cx="60" cy="60" r={RADIUS} fill="none"
              stroke="#10b981" strokeWidth="10" strokeLinecap="round"
              strokeDasharray={CIRC} strokeDashoffset={loading ? CIRC : offset}
              style={{ transition: 'stroke-dashoffset 0.6s ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-bold text-emerald-800" data-testid="present-today-percent">
              {pct}%
            </span>
          </div>
        </div>

        {/* Counts */}
        <div className="flex-shrink-0 sm:w-56">
          <p className="text-xs uppercase tracking-wide text-gray-500">Present Today (currently clocked in)</p>
          <div className="mt-1"><DisplayLinkMenu /></div>
          <p className="text-4xl font-bold text-gray-900 leading-tight" data-testid="present-today-count">{present}</p>
          <p className="mt-2 text-xs uppercase tracking-wide text-gray-500">Total Employees</p>
          <p className="text-3xl font-bold text-gray-700" data-testid="present-today-total">{total}</p>
        </div>
        <PresentStaffChips staff={data?.present_staff || []} />
      </div>

      {/* On-leave breakdown */}
      <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {LEAVE_CARDS.map((c) => (
          <div
            key={c.key}
            className="rounded-xl border border-gray-200 bg-white px-3 py-3 text-center"
            data-testid={`present-leave-${c.key}`}
          >
            <p className="text-2xl font-bold text-gray-900">{onLeave[c.key] ?? 0}</p>
            <p className="mt-0.5 text-xs text-gray-500">{c.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PresentTodayPanel;
